import React, { useRef, useState, useEffect, useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"

import useSentry from "Pods/Shared/Hooks/useSentry"
import useModelViewPing from "Pods/ThreeDStudio/Hooks/useModelViewPing"
import usePersistCameraPose from "Pods/ThreeDStudio/Hooks/usePersistCameraPose"
import LoadingState from "Pods/ThreeDStudio/Shared/LoadingState/LoadingState"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { setViewerFileFormat } from "Pods/ThreeDStudio/Store/actions/viewer"

import EnvironmentSettings from "./Components/EnvironmentSettings/EnvironmentSettings"
import {
  BACKGROUND_IMAGES_QUERY_KEY,
  fetchBackgroundImages,
} from "./Components/EnvironmentSettings/Hooks/useBackgroundImages"
import InnerViewerSidebar from "./Components/InnerViewerSidebar/InnerViewerSidebar"
import ModelDetailsCard from "./Components/ModelDetailsCard/ModelDetailsCard"
import RenderModeSelector from "./Components/RenderModeSelector/RenderModeSelector"
import useViewerHotkeys from "./Hooks/useViewerHotkeys"
import { applyInitialSettings } from "./Utils/applyInitialSettings"
import { applyTopologyMode } from "./Utils/applyTopologyMode"
import { reportModelLoadFailure } from "./Utils/reportModelLoadFailure"

function ViewerContent() {
  const [isEnvironmentSettingsOpen, setIsEnvironmentSettingsOpen] = useState(false)
  const handleEnvironmentSettingsClick = useCallback(() => {
    setIsEnvironmentSettingsOpen(prev => !prev)
  }, [])
  useViewerHotkeys({ onToggleEnvironmentSettings: handleEnvironmentSettingsClick })
  const queryClient = useQueryClient()
  const { captureException } = useSentry()
  // captureException's identity flips once Sentry finishes loading; keeping it
  // in a ref stops that from re-running the load effect and reloading the model.
  const captureExceptionRef = useRef(captureException)
  captureExceptionRef.current = captureException
  const [isInitialApplyPending, setIsInitialApplyPending] = useState(true)
  // The run whose mesh actually reached the screen. A plain painted/not-painted
  // flag is not enough: this hook runs before the load effect below, so on the
  // first render after a switch the flag would still describe the previous run
  // and the ping would fire for the new one before it paints. Comparing ids
  // makes the gate false from that very render. It also stays false when a load
  // fails, where the loader flag clears without anything being painted.
  const [paintedRunId, setPaintedRunId] = useState(null)
  const openedRunId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const uploadedModelUrl = useThreeDStudioStore(state => state.modelSettings.inputThreeDUrl)
  // An uploaded model wins over ui.openedModel below but does not clear it, so
  // the ping is gated on it too - otherwise it would record a view for an
  // off-screen run.
  useModelViewPing({ enabled: paintedRunId != null && paintedRunId === openedRunId && !uploadedModelUrl })
  const viewer = useViewerContext()
  usePersistCameraPose(viewer)
  const uploadedModelFormat = useThreeDStudioStore(state => state.viewer.fileFormat)
  const modelUrl = useThreeDStudioStore(state => state.ui.openedModel?.outputAssets?.[0]?.fileUrl)
  const materialPngUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'png')?.fileUrl
  )
  const materialMtlUrl = useThreeDStudioStore(
    state => state.ui.openedModel?.runExtraOutputs?.find(output => output.file_type === 'mtl')?.fileUrl
  )
  const settings = useThreeDStudioStore(state => state.ui?.openedModel?.settings)
  const material = useThreeDStudioStore(state => state.viewer?.material)
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  const isSmartLowPoly = useThreeDStudioStore(
    state => state.modelSettings.isSmartLowPoly || state.remeshSettings.isSmartLowPoly
  )

  const modelData = uploadedModelUrl
    ? { url: uploadedModelUrl, format: uploadedModelFormat }
    : { url: modelUrl, pngUrl: materialPngUrl, mtlUrl: materialMtlUrl }

  const currentLoadRequestIdRef = useRef(0)
  const currentModelIdRef = useRef(null)
  // Serializes viewer mutations. `loadModel` adds the mesh to the scene before
  // it resolves (and before the model lands in `getLoadedModels`), so a newer
  // request's `unloadAllModels` cannot remove an older mesh that is still
  // mid-load. Chaining each request guarantees only one load runs at a time, so
  // two models can never paint at once when the user switches rapidly.
  const loadChainRef = useRef(Promise.resolve())

  useEffect(() => {
    if (!viewer || viewer.isDisposed) {
      return
    }

    const currentViewer = viewer
    const { url, pngUrl, mtlUrl, format } = modelData
    const urlToLoad = (() => {
      const files = [url, pngUrl, mtlUrl].filter(Boolean)
      if (files.length === 1) {
        return files[0]
      }

      return files
    })()

    const requestId = currentLoadRequestIdRef.current + 1
    currentLoadRequestIdRef.current = requestId

    if (!urlToLoad) {
      loadChainRef.current = loadChainRef.current.then(() => {
        if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
          return
        }
        try {
          if (currentModelIdRef.current) {
            currentViewer.unloadModel(currentModelIdRef.current)
            currentModelIdRef.current = null
          } else {
            currentViewer.unloadAllModels()
          }
        } catch {
          // Ignore unload failures; viewer state will be corrected on next update.
        }
        setIsInitialApplyPending(false)
        setPaintedRunId(null)
      }).catch(() => {})
      return
    }

    setIsInitialApplyPending(true)
    setPaintedRunId(null)

    loadChainRef.current = loadChainRef.current.then(() => {
      // Superseded before this request reached the head of the chain: skip the
      // load entirely so rapid switching only ever loads the latest model.
      if (currentLoadRequestIdRef.current !== requestId || currentViewer.isDisposed) {
        return undefined
      }

      try {
        currentViewer.unloadAllModels()
        currentModelIdRef.current = null
      } catch {
        // Ignore unload failures; viewer state will be corrected on next update.
      }

      return Promise.all([
        currentViewer.loadModel(urlToLoad, undefined, format).catch((error) => {
          // Most often a CORS-blocked asset response: the model never paints and
          // the failure is otherwise invisible, so it goes to Sentry. Rejections
          // caused by the viewer being torn down mid-load are not real failures.
          if (currentViewer.isDisposed) {
            throw error
          }

          // Deliberately not awaited: the report runs a reachability probe to
          // tell a CORS block apart from a real outage, and the failure has to
          // surface now so the loader clears. It never rejects.
          reportModelLoadFailure(captureExceptionRef.current, error, {
            url: urlToLoad,
            format,
            providerModelId: openedModelId,
            isUploadedModel: Boolean(uploadedModelUrl),
          })

          throw error
        }),
        // Resolves the cached background-images list (fetches once if missing)
        // so applyInitialSettings can map surroundingId -> background_url.
        // Falls back to [] on failure to keep the rest of the apply unblocked.
        queryClient.ensureQueryData({
          queryKey: BACKGROUND_IMAGES_QUERY_KEY,
          queryFn: fetchBackgroundImages,
        }).catch(() => []),
      ])
        .then(([modelEntry, images]) => {
          if (currentLoadRequestIdRef.current !== requestId) {
            try {
              currentViewer.unloadModel(modelEntry.id)
            } catch {
              // Ignore unload failures for outdated model.
            }
            return
          }

          currentModelIdRef.current = modelEntry.id
          setViewerFileFormat(modelEntry.format)

          applyTopologyMode(currentViewer)
          applyInitialSettings(currentViewer, settings, images, material, openedModelId, isSmartLowPoly)
          requestAnimationFrame(() => {
            if (currentLoadRequestIdRef.current === requestId) {
              setIsInitialApplyPending(false)
              // openedRunId is read from the render that scheduled this load, so
              // it always names the run these assets belong to.
              setPaintedRunId(openedRunId ?? null)
            }
          })
        })
        .catch(() => {
          if (currentLoadRequestIdRef.current === requestId) {
            // Ignore load failure; viewer will attempt to load again on next update.
            setIsInitialApplyPending(false)
          }
        })
    }).catch(() => {})
  }, [
    viewer,
    modelData.url,
    modelData.pngUrl,
    modelData.mtlUrl,
    modelData.format,
  ])

  useEffect(() => {
    if (!isEnvironmentSettingsOpen) {
      return undefined
    }

    const controller = new AbortController()
    const { signal } = controller

    document.addEventListener('mousedown', (event) => {
      if (event.target.closest('.environment-settings')) {
        return
      }
      // The Bulb trigger toggles the popover itself; excluding it avoids a
      // close-then-reopen race between this listener and the button's onClick.
      if (event.target.closest('#inner-viewer-sidebar-bulb-button')) {
        return
      }

      setIsEnvironmentSettingsOpen(false)
    }, { capture: true, signal })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setIsEnvironmentSettingsOpen(false)
      }
    }, { signal })

    return () => controller.abort()
  }, [isEnvironmentSettingsOpen])

  return (
    <>
      <RenderModeSelector />
      <ModelDetailsCard />
      <EnvironmentSettings isOpen={isEnvironmentSettingsOpen} />
      <InnerViewerSidebar
        isEnvironmentSettingsOpen={isEnvironmentSettingsOpen}
        onEnvironmentSettingsClick={handleEnvironmentSettingsClick}
      />
      {isInitialApplyPending && <LoadingState />}
    </>
  )
}

export default ViewerContent
